import type {
  CompanyDocument,
  CompanyInvite,
  ConnectorType,
  Organization,
  TaskView,
  TeamMember,
  TenantInfo,
} from "@scoach/types";
import { api } from "../../lib/http.ts";

export interface CompanyDataResponse {
  organization: Organization | null;
  role: "admin" | "member" | null;
  functionalRole?: string | null;
  isSuperAdmin: boolean;
  documents: CompanyDocument[];
  members: TeamMember[];
}

export interface MemberOverviewResponse {
  member: {
    uid: string;
    name: string;
    email: string;
    role: string;
    companyRole: string;
    managerUid?: string;
    managerName?: string;
    allowedConnectors?: ConnectorType[];
  };
  tasks: TaskView[];
  meetings: Array<{
    id: string;
    title: string;
    client: string;
    date: string;
    meetingType?: string;
    stage?: string;
  }>;
  stats: {
    openTasksCount: number;
    completedTasksCount: number;
    totalMeetingsCount: number;
  };
}

export const companyApi = {
  async getMyOrganization(): Promise<CompanyDataResponse> {
    return api<CompanyDataResponse>("/organizations/me");
  },

  async resolveSubdomain(subdomain: string): Promise<TenantInfo | null> {
    try {
      return await api<TenantInfo>(`/organizations/resolve/${encodeURIComponent(subdomain)}`);
    } catch {
      return null;
    }
  },

  async createOrganization(data: {
    name: string;
    domain?: string;
    subdomain?: string;
    allowedDomains?: string[];
    description?: string;
    productsServices?: string;
    valueProposition?: string;
    targetAudience?: string;
    competitors?: string;
    pitchKeywords?: string;
    roles?: string[];
    proceduresText?: string;
    myRole?: string;
    mondayApiKey?: string;
    integrations?: any;
  }): Promise<{ organization: Organization; role: string; functionalRole?: string }> {
    return api<{ organization: Organization; role: string; functionalRole?: string }>("/organizations", {
      method: "POST",
      body: data,
    });
  },

  async updateOrganization(
    data: Partial<Omit<Organization, "id" | "createdAt" | "createdByUid" | "storeName">>,
  ): Promise<{ organization: Organization }> {
    return api<{ organization: Organization }>("/organizations/me", {
      method: "PATCH",
      body: data,
    });
  },

  async updateMyRole(role: string): Promise<{ ok: boolean; functionalRole: string }> {
    return api<{ ok: boolean; functionalRole: string }>("/organizations/me/my-role", {
      method: "PATCH",
      body: { role },
    });
  },

  async uploadDocument(file: File, category?: "procedure" | "knowledge"): Promise<CompanyDocument> {
    const fd = new FormData();
    fd.append("file", file, file.name);
    if (category) fd.append("category", category);
    return api<CompanyDocument>("/organizations/me/documents", {
      method: "POST",
      body: fd,
    });
  },

  async addDocumentLink(
    url: string,
    driveToken?: string,
    category?: "procedure" | "knowledge",
  ): Promise<CompanyDocument> {
    const headers: Record<string, string> = {};
    if (driveToken) headers["X-Drive-Token"] = driveToken;
    return api<CompanyDocument>("/organizations/me/documents/link", {
      method: "POST",
      body: { url, category },
      headers,
    });
  },

  async deleteDocument(id: string): Promise<void> {
    await api<void>(`/organizations/me/documents/${id}`, {
      method: "DELETE",
    });
  },

  // Member management & invites
  async inviteMember(params: {
    email: string;
    name?: string;
    companyRole?: "admin" | "member";
    functionalRole?: string;
    managerUid?: string;
    allowedConnectors?: ConnectorType[];
  }): Promise<{ ok: boolean; invite: CompanyInvite; inviteUrl?: string; emailDispatched?: boolean; preApproved?: boolean }> {
    return api<{ ok: boolean; invite: CompanyInvite; inviteUrl?: string; emailDispatched?: boolean; preApproved?: boolean }>("/organizations/me/members/invite", {
      method: "POST",
      body: params,
    });
  },

  async updateMember(
    memberUid: string,
    params: {
      companyRole?: "admin" | "member";
      functionalRole?: string;
      managerUid?: string | null;
      allowedConnectors?: ConnectorType[];
    },
  ): Promise<{ ok: boolean }> {
    return api<{ ok: boolean }>(`/organizations/me/members/${encodeURIComponent(memberUid)}`, {
      method: "PATCH",
      body: params,
    });
  },

  async removeMember(memberUid: string): Promise<{ ok: boolean }> {
    return api<{ ok: boolean }>(`/organizations/me/members/${encodeURIComponent(memberUid)}`, {
      method: "DELETE",
    });
  },

  async getMemberOverview(memberUid: string): Promise<MemberOverviewResponse> {
    return api<MemberOverviewResponse>(`/organizations/me/members/${encodeURIComponent(memberUid)}/overview`);
  },

  // Monday.com connector
  async testMondayConnection(apiKey: string): Promise<{ ok: boolean; user: { id: string; name: string; email: string; accountName?: string } }> {
    return api("/organizations/me/connectors/monday/test", {
      method: "POST",
      body: { apiKey },
    });
  },

  async getMondayBoards(apiKey?: string): Promise<{ boards: Array<{ id: string; name: string; type: string; itemsCount?: number; description?: string }> }> {
    const headers: Record<string, string> = {};
    if (apiKey) headers["x-monday-token"] = apiKey;
    return api("/organizations/me/connectors/monday/boards", { headers });
  },

  async getMondayDocs(apiKey?: string): Promise<{ docs: Array<{ id: string; title: string; docKind?: string; createdAt?: string }> }> {
    const headers: Record<string, string> = {};
    if (apiKey) headers["x-monday-token"] = apiKey;
    return api("/organizations/me/connectors/monday/docs", { headers });
  },

  async importMondayDocument(params: {
    type: "doc" | "board" | "url";
    id?: string;
    url?: string;
    category?: "procedure" | "knowledge";
    apiKey?: string;
  }): Promise<CompanyDocument> {
    return api<CompanyDocument>("/organizations/me/connectors/monday/import", {
      method: "POST",
      body: params,
    });
  },

  // SharePoint connector
  async importSharePointDocument(params: {
    url: string;
    token?: string;
    category?: "procedure" | "knowledge";
  }): Promise<CompanyDocument> {
    return api<CompanyDocument>("/organizations/me/connectors/sharepoint/import", {
      method: "POST",
      body: params,
    });
  },
};
